import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { Observable, map, catchError, of } from 'rxjs';
import { dbConst } from './databaseConstants';
import { FirestoreCrudService } from './crudService';
import { AuthService } from '../auth.service';
import { ISupportThread } from 'app/models/support';

@Injectable({ providedIn: 'root' })
export class SupportThreadService {
  private crudService: FirestoreCrudService<ISupportThread>;

  constructor(private firestore: Firestore, private authService: AuthService) {
    this.crudService = new FirestoreCrudService<ISupportThread>(
      firestore,
      authService,
      dbConst.supportThreads,
    );
  }

  getNewId(): string {
    return this.crudService.getNewId();
  }

  getThread(id: string): Observable<ISupportThread> {
    return this.crudService.get(id);
  }

  async getThreadOnce(id: string): Promise<ISupportThread> {
    return this.crudService.getOnce(id);
  }

  getThreadsForUser(userId: string, take: number = 100): Observable<ISupportThread[]> {
    const listRef = collection(this.firestore, dbConst.supportThreads);
    const q = query(
      listRef,
      where('userId', '==', userId),
      orderBy('lastMessageAt', 'desc'),
      limit(take),
    );
    return collectionData(q, { idField: 'id' }).pipe(
      map((items) => items.map((it) => this.convertTimestamps(it))),
      catchError((err) => {
        console.error('getThreadsForUser failed', err);
        return of([] as ISupportThread[]);
      }),
    ) as Observable<ISupportThread[]>;
  }

  getAllThreadsForDev(take: number = 200): Observable<ISupportThread[]> {
    const listRef = collection(this.firestore, dbConst.supportThreads);
    const q = query(listRef, orderBy('lastMessageAt', 'desc'), limit(take));
    return collectionData(q, { idField: 'id' }).pipe(
      // Drafts are only visible to the owner — the dev inbox must never show
      // half-written inquiries.
      map((items) => items.filter((it) => (it as Partial<ISupportThread>).status !== 'draft')),
      map((items) => items.map((it) => this.convertTimestamps(it))),
      catchError((err) => {
        console.error('getAllThreadsForDev failed', err);
        return of([] as ISupportThread[]);
      }),
    ) as Observable<ISupportThread[]>;
  }

  async addThread(thread: ISupportThread): Promise<ISupportThread> {
    return this.crudService.add(thread, thread.id);
  }

  /** Generic field update on a thread (used by drafts and draft-send). */
  async updateThread(id: string, patch: Partial<ISupportThread>, uid: string): Promise<void> {
    return this.crudService.softUpdate(id, { ...patch, updatedAt: new Date() }, uid);
  }

  async assignTo(id: string, devUid: string, devEmail: string): Promise<void> {
    return this.crudService.softUpdate(
      id,
      { assignedTo: devUid, assignedToEmail: devEmail, updatedAt: new Date() },
      devUid,
    );
  }

  async markRead(id: string, role: 'user' | 'developer'): Promise<void> {
    const docRef = doc(this.firestore, `${dbConst.supportThreads}/${id}`);
    const patch = role === 'user' ? { unreadByUser: false } : { unreadByDev: false };
    await updateDoc(docRef, patch);
  }

  async archiveThread(id: string, archived: boolean): Promise<void> {
    const docRef = doc(this.firestore, `${dbConst.supportThreads}/${id}`);
    await updateDoc(docRef, { archived, updatedAt: new Date() });
  }

  async deleteThread(id: string): Promise<void> {
    return this.crudService.delete(id);
  }

  async applyReplyUpdate(
    id: string,
    changes: Partial<ISupportThread>,
    uid: string,
  ): Promise<void> {
    return this.crudService.softUpdate(id, changes, uid);
  }

  async addTag(id: string, tag: string, uid: string): Promise<void> {
    const snap = await getDoc(doc(this.firestore, `${dbConst.supportThreads}/${id}`));
    if (!snap.exists()) return;
    const existing = (snap.data() as ISupportThread).tags ?? [];
    if (existing.includes(tag)) return;
    await this.crudService.softUpdate(id, { tags: [...existing, tag] }, uid);
  }

  private convertTimestamps(obj: any): ISupportThread {
    if (!obj) return obj;
    const out: any = { ...obj };
    for (const key of Object.keys(out)) {
      const v = out[key];
      if (v instanceof Timestamp) {
        out[key] = v.toDate();
      } else if (v && typeof v === 'object' && typeof v.seconds === 'number' && typeof v.nanoseconds === 'number') {
        out[key] = new Timestamp(v.seconds, v.nanoseconds).toDate();
      }
    }
    return out as ISupportThread;
  }
}
